import { Building, Building2, Users } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import type { LocalAccountEconomicGroup } from '@/context/create-account-flow';

interface EconomicGroupCardProps {
  economicGroup: LocalAccountEconomicGroup | null;
  currentAccountTaxId?: string;
  onManageGroups: () => void;
  canEditGroup?: boolean;
}

const formatCnpj = (value: string) => {
  const digits = value.replace(/\D/g, '');
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

export function EconomicGroupCard({
  economicGroup,
  currentAccountTaxId,
  onManageGroups,
  canEditGroup = true,
}: EconomicGroupCardProps) {
  const normalizedCurrentTaxId = currentAccountTaxId?.replace(/\D/g, '');
  const hasHolding = !!economicGroup?.economic_group_holding;
  const hasControlled =
    economicGroup?.economic_group_controlled && economicGroup.economic_group_controlled.length > 0;
  const isEmpty = !hasHolding && !hasControlled;

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Grupo Econômico</CardTitle>
              {!isEmpty && (
                <p className="text-xs text-muted-foreground mt-1">
                  {hasHolding && hasControlled
                    ? 'Holding e empresas controladas'
                    : hasHolding
                      ? 'Apenas holding'
                      : 'Apenas controladas'}
                </p>
              )}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Users className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">Nenhum grupo econômico vinculado</p>
            {canEditGroup && (
              <Button variant="outline" size="sm" className="mt-3" onClick={onManageGroups}>
                Vincular grupo
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Holding */}
            {hasHolding && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-primary" />
                  <p className="text-xs font-medium text-muted-foreground uppercase">Holding</p>
                </div>
                {(() => {
                  const holdingTaxId = economicGroup?.economic_group_holding?.taxId?.replace(
                    /\D/g,
                    '',
                  );
                  const isCurrent =
                    normalizedCurrentTaxId && holdingTaxId === normalizedCurrentTaxId;
                  return (
                    <div
                      className={`flex items-center gap-2 rounded-lg border p-3 ${isCurrent ? 'bg-blue-50 border-blue-200' : 'bg-muted/30'}`}
                    >
                      <Building2
                        className={`h-4 w-4 flex-shrink-0 ${isCurrent ? 'text-blue-600' : 'text-muted-foreground'}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p
                          className={`font-medium text-sm truncate ${isCurrent ? 'text-blue-800' : ''}`}
                        >
                          {economicGroup?.economic_group_holding?.name}
                        </p>
                        <p
                          className={`text-xs font-mono mt-1 ${isCurrent ? 'text-blue-600' : 'text-muted-foreground'}`}
                        >
                          {formatCnpj(economicGroup?.economic_group_holding?.taxId ?? '')}
                        </p>
                      </div>
                      {isCurrent && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-blue-100 text-blue-700 border-blue-300 flex-shrink-0"
                        >
                          Esta conta
                        </Badge>
                      )}
                    </div>
                  );
                })()}
              </div>
            )}

            {hasHolding && hasControlled && <Separator />}

            {/* Controlled companies */}
            {hasControlled && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <p className="text-xs font-medium text-muted-foreground uppercase">
                    Empresas Controladas
                  </p>
                  <Badge variant="secondary" className="text-xs">
                    {economicGroup?.economic_group_controlled?.length}
                  </Badge>
                </div>
                <div className="space-y-2">
                  {economicGroup?.economic_group_controlled?.map((company) => {
                    const companyTaxId = company.taxId.replace(/\D/g, '');
                    const isCurrent =
                      normalizedCurrentTaxId && companyTaxId === normalizedCurrentTaxId;
                    return (
                      <div
                        key={company.taxId}
                        className={`flex items-center gap-2 rounded-lg border p-3 ${isCurrent ? 'bg-blue-50 border-blue-200' : 'bg-background'}`}
                      >
                        <Building2
                          className={`h-4 w-4 flex-shrink-0 ${isCurrent ? 'text-blue-600' : 'text-muted-foreground'}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p
                            className={`font-medium text-sm truncate ${isCurrent ? 'text-blue-800' : ''}`}
                          >
                            {company.name}
                          </p>
                          <p
                            className={`text-xs font-mono mt-1 ${isCurrent ? 'text-blue-600' : 'text-muted-foreground'}`}
                          >
                            {formatCnpj(company.taxId)}
                          </p>
                        </div>
                        {isCurrent && (
                          <Badge
                            variant="outline"
                            className="text-xs bg-blue-100 text-blue-700 border-blue-300 flex-shrink-0"
                          >
                            Esta conta
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
